import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { formatPrice } from "@/lib/api";
import { Minus, Plus, Trash2, ShoppingBag, Bookmark, CornerUpLeft, Truck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { JerseyBackPreview } from "@/components/JerseyBackPreview";

const isCustom = (it) => Boolean(it.custom_name || it.custom_number);

export default function CartDrawer() {
  const { items, open, setOpen, removeItem, updateQty, subtotal, originalSubtotal, savings, net, discountCents, saved, saveForLater, moveToCart, removeSaved } = useCart();
  const { freeShipThreshold } = useSettings();
  const navigate = useNavigate();

  const remainingFree = freeShipThreshold > 0 ? Math.max(0, freeShipThreshold - subtotal) : 0;
  const freeUnlocked = freeShipThreshold > 0 && subtotal >= freeShipThreshold;
  const freeProgress = freeShipThreshold > 0 ? Math.min(100, (subtotal / freeShipThreshold) * 100) : 0;

  const checkout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="cart-drawer"
        className="w-full sm:max-w-md rounded-none border-l border-white/10 bg-background text-white flex flex-col p-0"
      >
        <SheetHeader className="p-6 border-b border-white/10">
          <SheetTitle className="font-anton text-2xl uppercase tracking-tight text-white">
            Your Cart ({items.length})
          </SheetTitle>
          <SheetDescription className="sr-only">Review the items in your shopping cart and proceed to checkout.</SheetDescription>
        </SheetHeader>

        {items.length === 0 && saved.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center px-6">
            <ShoppingBag className="w-12 h-12 text-white/20" />
            <p className="text-white/50 font-medium">Your cart is empty.</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <div className="divide-y divide-white/10">
              {items.map((it) => (
                <div key={it.key} data-testid="cart-item" className="flex gap-4 p-6">
                  <div className="w-20 h-24 bg-neutral-900 overflow-hidden flex-shrink-0">
                    {isCustom(it) ? (
                      <JerseyBackPreview data-testid="cart-item-preview" name={it.custom_name} number={it.custom_number} garment={it.garment} className="h-full" />
                    ) : (
                      it.image && <img src={it.image} alt={it.title} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 flex flex-col">
                    <p className="font-bold uppercase text-sm tracking-tight leading-tight text-white">{it.title}</p>
                    <p className="text-xs text-white/50 mt-1">Size: {it.size}{it.custom_color ? ` · ${it.custom_color}` : ""}</p>
                    {(it.custom_name || it.custom_number) && (
                      <p data-testid="cart-item-custom" className="text-xs text-primary font-bold mt-0.5 uppercase">
                        {it.custom_name}{it.custom_number ? ` #${it.custom_number}` : ""}
                      </p>
                    )}
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center border border-white/20">
                        <button data-testid="cart-qty-dec" onClick={() => updateQty(it.key, -1)} className="px-2 py-1 hover:bg-primary hover:text-black text-white">
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="px-3 text-sm font-bold text-white">{it.quantity}</span>
                        <button data-testid="cart-qty-inc" onClick={() => updateQty(it.key, 1)} className="px-2 py-1 hover:bg-primary hover:text-black text-white">
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <span className="font-bold text-sm text-white">
                        {discountCents > 0 && (
                          <span className="text-white/40 line-through mr-2 font-normal">{formatPrice(it.price * it.quantity)}</span>
                        )}
                        {formatPrice(net(it.price) * it.quantity)}
                      </span>
                    </div>
                    <button
                      data-testid="save-for-later"
                      onClick={() => saveForLater(it.key)}
                      className="mt-3 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold text-white/40 hover:text-primary transition-colors self-start"
                    >
                      <Bookmark className="w-3.5 h-3.5" /> Save for later
                    </button>
                  </div>
                  <button data-testid="cart-remove" onClick={() => removeItem(it.key)} className="text-white/30 hover:text-primary self-start">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Saved for later */}
            {saved.length > 0 && (
              <div className="border-t border-white/10" data-testid="saved-section">
                <p className="px-6 pt-6 pb-2 text-xs uppercase tracking-[0.25em] font-bold text-white/50">
                  Saved for later ({saved.length})
                </p>
                <div className="divide-y divide-white/10">
                  {saved.map((it) => (
                    <div key={it.key} data-testid="saved-item" className="flex gap-4 px-6 py-5">
                      <div className="w-16 h-20 bg-neutral-900 overflow-hidden flex-shrink-0">
                        {isCustom(it) ? (
                          <JerseyBackPreview name={it.custom_name} number={it.custom_number} garment={it.garment} className="h-full" />
                        ) : (
                          it.image && <img src={it.image} alt={it.title} className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="flex-1 flex flex-col">
                        <p className="font-bold uppercase text-sm tracking-tight leading-tight text-white">{it.title}</p>
                        <p className="text-xs text-white/50 mt-1">Size: {it.size}{it.custom_color ? ` · ${it.custom_color}` : ""}</p>
                        {(it.custom_name || it.custom_number) && (
                          <p className="text-xs text-primary font-bold mt-0.5 uppercase">
                            {it.custom_name}{it.custom_number ? ` #${it.custom_number}` : ""}
                          </p>
                        )}
                        <div className="mt-auto flex items-center justify-between pt-2">
                          <button
                            data-testid="move-to-cart"
                            onClick={() => moveToCart(it.key)}
                            className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold text-primary hover:text-white transition-colors"
                          >
                            <CornerUpLeft className="w-3.5 h-3.5" /> Move to cart
                          </button>
                          <span className="font-bold text-sm text-white">
                            {discountCents > 0 && (
                              <span className="text-white/40 line-through mr-2 font-normal">{formatPrice(it.price)}</span>
                            )}
                            {formatPrice(net(it.price))}
                          </span>
                        </div>
                      </div>
                      <button data-testid="saved-remove" onClick={() => removeSaved(it.key)} className="text-white/30 hover:text-primary self-start">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {items.length > 0 && (
          <div className="border-t border-white/10 p-6 space-y-4">
            {freeShipThreshold > 0 && (
              <div data-testid="cart-free-ship" className="space-y-2">
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/70">
                  <Truck className="w-4 h-4 text-primary" />
                  {freeUnlocked ? (
                    <span className="text-primary">Free Standard shipping unlocked</span>
                  ) : (
                    <span>Add <span className="text-primary">{formatPrice(remainingFree)}</span> for free shipping</span>
                  )}
                </p>
                <div className="h-1.5 w-full bg-white/10 overflow-hidden">
                  <div className="h-full bg-primary transition-all duration-300" style={{ width: `${freeProgress}%` }} />
                </div>
              </div>
            )}
            {savings > 0 && (
              <div data-testid="cart-savings" className="flex items-center justify-between text-sm text-primary font-bold uppercase tracking-widest">
                <span>You save</span>
                <span>−{formatPrice(savings)}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm uppercase tracking-widest font-bold text-white/60">Subtotal</span>
              <span data-testid="cart-subtotal" className="font-anton text-2xl text-white">{formatPrice(subtotal)}</span>
            </div>
            <button
              data-testid="checkout-button"
              onClick={checkout}
              className="w-full bg-primary text-black hover:bg-white px-8 py-4 font-bold uppercase tracking-[0.2em] text-sm transition-colors active:scale-95"
            >
              Checkout
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
